// src/feature-module/support/StockDriftFix.tsx
//
// The inline fix for the health panel's stock_vs_ledger check.
//
// The check already knew which locations had drifted and by how much; without
// this the supporter read that, then went somewhere else to do something about
// it. Finding a fault and offering its remedy in the same place is the whole
// point of the panel — a diagnosis with no adjacent action is just a nicer way
// of being told bad news.
//
// Only `fail` rows get a button. `warn` rows are locations with no movement
// history at all (stock imported before the ledger was enforced): reconciling
// those would write a DISCREPANCY movement for the entire balance, manufacturing
// a fake correction for something that was never wrong. That distinction is the
// reason the backend splits untracked from drifted, and throwing a Fix button on
// both would quietly undo it.

import React, { useState } from "react";
import { Table, Button, Modal, Form, Input, Alert, Tag, Space, Typography, message } from "antd";
import { ToolOutlined } from "@ant-design/icons";
import { useReconcileStockLedgerMutation } from "../../core/redux/services/adminSupportApi";

const { Text } = Typography;

export interface StockDriftSample {
  tenantProductVariantId: string;
  storeId: string | null;
  warehouseId: string | null;
  balance: string;
  ledger: string;
  movements: number;
}

function errorText(e: any, fallback: string) {
  return e?.data?.message || e?.error || fallback;
}

const shortId = (v?: string | null) => (v ? `…${v.slice(-8)}` : "—");

/**
 * These arrive as Postgres NUMERIC cast to text, so a whole number reaches us as
 * "114.000000000000000000000000000000". Rendering that raw turns a two-row table
 * into something nobody can read a quantity off. Trailing zeros are dropped but
 * genuine decimals are kept — stock can legitimately be fractional.
 */
const qty = (v: string | number | null | undefined) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v ?? "—");
  return Number.isInteger(n) ? String(n) : String(parseFloat(n.toFixed(4)));
};

export const StockDriftFix: React.FC<{
  tenantId: string;
  samples: StockDriftSample[];
  /** When present, each repair is attributed to this case. */
  caseId?: string;
}> = ({ tenantId, samples, caseId }) => {
  const [target, setTarget] = useState<StockDriftSample | null>(null);
  const [preview, setPreview] = useState<any>(null);
  const [reconcile, { isLoading }] = useReconcileStockLedgerMutation();
  const [form] = Form.useForm();

  const close = () => {
    setTarget(null);
    setPreview(null);
    form.resetFields();
  };

  const run = async (dryRun: boolean) => {
    if (!target) return;
    try {
      const v = await form.validateFields();
      const res = await reconcile({
        tenantId,
        tenantProductVariantId: target.tenantProductVariantId,
        storeId: target.storeId,
        warehouseId: target.warehouseId,
        caseId,
        dryRun,
        reason: v.reason,
      }).unwrap();
      if (dryRun) {
        setPreview(res);
        message.info(res.message ?? "Dry run complete.");
      } else {
        message.success(res.message ?? "Ledger reconciled.");
        close();
      }
    } catch (e: any) {
      if (e?.errorFields) return; // inline validation, already shown
      message.error(errorText(e, "Could not reconcile this location."));
    }
  };

  return (
    <>
      <Table<StockDriftSample>
        size="small"
        rowKey={(r) => `${r.tenantProductVariantId}:${r.storeId ?? ""}:${r.warehouseId ?? ""}`}
        pagination={false}
        dataSource={samples}
        columns={[
          {
            title: "Variant",
            dataIndex: "tenantProductVariantId",
            render: (v: string) => <Text code copyable={{ text: v }}>{shortId(v)}</Text>,
          },
          {
            title: "Location",
            render: (_: unknown, r) =>
              r.storeId ? (
                <Tag>store {shortId(r.storeId)}</Tag>
              ) : r.warehouseId ? (
                <Tag color="blue">warehouse {shortId(r.warehouseId)}</Tag>
              ) : (
                <Tag color="red">unplaced</Tag>
              ),
          },
          { title: "Balance", dataIndex: "balance", align: "right" as const, render: qty },
          { title: "Ledger", dataIndex: "ledger", align: "right" as const, render: qty },
          {
            title: "Drift",
            align: "right" as const,
            render: (_: unknown, r) => {
              const d = Number(r.balance) - Number(r.ledger);
              return <Tag color={d === 0 ? "default" : "red"}>{d > 0 ? `+${d}` : d}</Tag>;
            },
          },
          { title: "Movements", dataIndex: "movements", align: "right" as const },
          {
            title: "",
            width: 110,
            render: (_: unknown, r) => (
              <Button size="small" icon={<ToolOutlined />} onClick={() => setTarget(r)}>
                Reconcile
              </Button>
            ),
          },
        ]}
      />

      <Modal
        title="Reconcile this location's ledger"
        open={!!target}
        onCancel={close}
        footer={
          <Space>
            <Button onClick={close}>Cancel</Button>
            {/* Equal billing with apply, as on every other repair: previewing is
                the normal thing to do first, not a lesser option tucked away. */}
            <Button onClick={() => run(true)} loading={isLoading}>
              Preview
            </Button>
            <Button type="primary" danger onClick={() => run(false)} loading={isLoading}>
              Apply
            </Button>
          </Space>
        }
      >
        {target && (
          <>
            <Alert
              className="mb-3"
              type="warning"
              showIcon
              message="This writes a correcting movement, it does not erase history"
              description={
                <>
                  The balance stays as it is — {qty(target.balance)} — and a DISCREPANCY movement of{" "}
                  <strong>{Number(target.balance) - Number(target.ledger)}</strong> is written so the ledger sums to it.
                  The gap itself is preserved as an explicit, attributable correction rather than being papered over.
                </>
              }
            />
            <Form form={form} layout="vertical">
              <Form.Item
                name="reason"
                label="Reason"
                extra="Recorded against your account and kept for review. Minimum 12 characters."
                rules={[{ required: true, min: 12, message: "Give a reason of at least 12 characters." }]}
              >
                <Input.TextArea
                  rows={2}
                  placeholder="e.g. Drift from the August import, confirmed against the physical count"
                />
              </Form.Item>
            </Form>
            {preview && (
              <Alert
                type="info"
                showIcon
                message="Dry run"
                description={<pre style={{ margin: 0, whiteSpace: "pre-wrap", fontSize: 12 }}>{JSON.stringify(preview, null, 2)}</pre>}
              />
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default StockDriftFix;
